import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { FaMicrophone } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";

import Navbar from "../../component/Navbar/Navbar";
import Footer from "../../component/Footer/Footer";
import { useSmartDownload } from "../../hooks/useSmartDownload";

import heroImg from "./Assets/image.png";
import WhatItIsSection from "./WhatItIsSection";
import HowItWorksSection from "./HowItWorksSection";
import TranscriptionStepSection from "./TranscriptionStepSection";
import WritingWorkflowSection from "./WritingWorkflowSection";
import VsDictationSection from "./VsDictationSection";
import WhyItChangesSection from "./WhyItChangesSection";
import FAQSection from "./FAQSection";
import TryZeroTypingSection from "./TryZeroTypingSection";
import { GRADIENT_TEXT } from "./sections";

const STATS = [
  { value: "3×", label: "Faster than typing" },
  { value: "99%", label: "Uptime guaranteed" },
  { value: "10k+", label: "Active users" },
];

const HowVoiceTypingWorks = () => {
  const navigate = useNavigate();
  const smartDownload = useSmartDownload();

  return (
    <>
      <Helmet>
        <title>How AI Voice Typing Actually Works | Zero Typing AI</title>
        <meta
          name="description"
          content="AI voice typing is more than speech-to-text. See how your voice is captured, transcribed, and refined into clean, ready-to-use writing with Zero Typing AI."
        />
        <link
          rel="canonical"
          href="https://zerotyping.ai/how-ai-voice-typing-works"
        />
      </Helmet>

      <div className="relative overflow-hidden w-full bg-[#C9E9FC] border-b-[1.25px] border-[#E6ECF5] font-inter">
        {/* Floating colour glow — same orbiting-circle treatment as the Home hero */}
        <div
          className="pointer-events-none absolute inset-0 z-0 hidden md:block"
          aria-hidden="true"
        >
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 1440 774"
            preserveAspectRatio="none"
          >
            <path
              id="voiceOrbitPath"
              d="M0,0 H1440 V774 H0 Z"
              fill="none"
              stroke="transparent"
            />

            <circle r="300" fill="url(#voiceGlow)" filter="url(#voiceBlur)">
              <animateMotion repeatCount="indefinite" dur="20s" rotate="auto">
                <mpath href="#voiceOrbitPath" />
              </animateMotion>
            </circle>

            <circle r="300" fill="url(#voiceGlow)" filter="url(#voiceBlur)">
              <animateMotion
                repeatCount="indefinite"
                dur="20s"
                rotate="auto"
                keyPoints="1;0"
                keyTimes="0;1"
              >
                <mpath href="#voiceOrbitPath" />
              </animateMotion>
            </circle>

            <defs>
              <radialGradient id="voiceGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#3AB1F6" stopOpacity=".9" />
                <stop offset="100%" stopColor="#3AB1F6" stopOpacity="0" />
              </radialGradient>
              <filter
                id="voiceBlur"
                x="-100%"
                y="-100%"
                width="300%"
                height="300%"
              >
                <feGaussianBlur in="SourceGraphic" stdDeviation="20" />
              </filter>
            </defs>
          </svg>
        </div>

        <Navbar />

        {/* Hero */}
        <div
          id="overview"
          className="max-w-[1440px] mx-auto pt-28 md:pt-36 pb-16 px-6 md:px-12 lg:px-[101px] relative z-10"
        >
          <div className="flex flex-col lg:flex-row items-start justify-between gap-10 lg:gap-[40px] xl:gap-[48px]">
            {/* Left: copy + actions */}
            <div className="w-full lg:w-[510px] xl:w-[530px] shrink-0 flex flex-col items-start text-left">
              {/* Eyebrow pill */}
              <span className="inline-flex items-center gap-[8px] rounded-full border-[1.07px] border-white/70 bg-white/70 px-[14px] py-[7px] backdrop-blur-sm">
                <span
                  aria-hidden="true"
                  className="h-[7px] w-[7px] shrink-0 rounded-full bg-gradient-to-r from-[#3AB1F6] to-[#407BFF]"
                />
                <span className="font-jakarta text-[12px] font-semibold leading-[18px] tracking-[0.01em] text-[#0A0D16B2]">
                  The Technology Behind Zero Typing AI
                </span>
              </span>

              <h1 className="mt-[21.46px] text-[#0A0D16] text-[36px] sm:text-[44px] lg:text-[50px] font-bold leading-[1.2] lg:leading-[62px] tracking-[-1.35px] font-jakarta">
                How AI Voice Typing Actually Works
              </h1>

              <div className="mt-[25.76px] flex flex-col gap-[25.76px] w-full max-w-[560px] font-inter text-[16px] font-normal leading-[29.6px] tracking-[0px] text-[#0A0D16B2]">
                <p>
                  If you&apos;ve ever spoken into a microphone and watched your
                  words appear as a polished paragraph on your screen,
                  you&apos;ve experienced AI voice typing.
                </p>
                <p>
                  But modern voice typing is more than simply converting speech
                  into text. Tools such as Zero Typing AI combine voice input
                  with AI-powered text processing to help turn natural speech
                  into usable written content. Instead of stopping at a raw
                  transcript, the technology can help clean up grammar, improve
                  phrasing, format text, and organize ideas.
                </p>
              </div>

              {/* Action buttons */}
              <div className="w-full flex flex-wrap lg:flex-nowrap items-center justify-center lg:justify-start gap-3 pt-[30.05px]">
                <button
                  type="button"
                  onClick={() => navigate("/demo")}
                  className="shrink-0 whitespace-nowrap bg-white hover:bg-slate-50 text-[#3AB1F6] font-semibold text-[14px] sm:text-[15px] px-5 py-3 rounded-full flex items-center gap-2.5 shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 cursor-pointer"
                >
                  <span className="w-7 h-7 rounded-full bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] flex items-center justify-center text-white shrink-0">
                    <FaMicrophone className="w-[14px] h-[14px]" />
                  </span>
                  <span>Try Zero Typing Free</span>
                </button>

                <button
                  type="button"
                  onClick={() => smartDownload("how_voice_typing_works_hero")}
                  className="shrink-0 whitespace-nowrap bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#33A2E5] hover:to-[#386FE8] text-white font-semibold text-[14px] sm:text-[15px] px-5 py-3 rounded-full flex items-center gap-2.5 shadow-[0_4px_18px_rgba(58,177,246,0.35)] hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 cursor-pointer"
                >
                  <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-[#3AB1F6] shrink-0">
                    <FiDownload className="w-[14px] h-[14px]" />
                  </span>
                  <span>Download Zero Typing</span>
                </button>
              </div>
            </div>

            {/* Right: product visual + stats */}
            <div className="w-full flex-1 min-w-0 flex flex-col">
              <img
                src={heroImg}
                width={3102}
                height={1861}
                alt="Zero Typing AI dictating a reply inside a desktop mail app, with Organise Thoughts and Professional writing styles applied"
                className="animate-float block w-full h-auto"
              />

              <div className="mt-[25.76px] border-t-[1.07px] border-[#0A0D1614] pt-[25.76px]">
                <div className="grid grid-cols-3 gap-[12px]">
                  {STATS.map(({ value, label }) => (
                    <div key={label}>
                      <p className="font-jakarta text-[30px] sm:text-[38px] lg:text-[44px] font-bold leading-[1.15] tracking-[-0.8px]">
                        <span className={GRADIENT_TEXT}>{value}</span>
                      </p>
                      <p className="mt-[6px] font-inter text-[14px] sm:text-[15px] lg:text-[16px] font-normal leading-[24px] tracking-[0px] text-[#0A0D1699]">
                        {label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article body */}
      <div className="w-full bg-white font-inter">
        <WhatItIsSection />
        <HowItWorksSection />
        <TranscriptionStepSection />
        <WritingWorkflowSection />
        <VsDictationSection />
        <WhyItChangesSection />
        <FAQSection />
        <TryZeroTypingSection />
      </div>

      <Footer />
    </>
  );
};

export default HowVoiceTypingWorks;
